import {
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import { AddCircle, CloudDownload, DragIndicator, Edit, Label, RemoveCircle, SwapHoriz } from '@material-ui/icons'
import { FlexHorizontal, FullModal, IBoard, LoadMask, MessageCard, Spacer, useToasts } from '@vestaboard/installables'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { csvDownload, csvFormat } from '../../../utils'
import { CreateMessageModal, MessageModal, useCreateMessage } from '../../message-v2'

import { Alert } from '@material-ui/lab'
import { batch } from 'cartesian-js'
import { uniq } from 'lodash'
import InfiniteScroll from 'react-infinite-scroller'
import { useHistory } from 'react-router'
import { Checkbox } from '../../../ui/Checkbox'
import { CsvImportButton } from '../../../ui/CsvImportButton'
import { DraggableTableRow } from '../../../ui/DraggableTableRow'
import { DroppableTableBody } from '../../../ui/DropableTableBody'
import { characterCodesToText } from '../../../utils/characterCodesToText'
import { FormattedDateTime } from '../../formatted-date-time'
import { BulkTags } from '../../message-v2/components/BulkTags'
import { useBulkTagMessages } from '../../message-v2/hooks/useBulkTagMessages'
import { GetMessageSetV2_messageSet_MessageSetV2 } from '../hooks/__generated__/GetMessageSetV2'
import { useCreateMessageSetVersion } from '../hooks/useCreateMessageSetVersion'
import { useGetMessageSetVersion } from '../hooks/useGetMesssageSetVersion'
import { useListMessageSetVersions } from '../hooks/useListMessageSetVersions'
import { usePublishMessageSetVersion } from '../hooks/usePublishMessageSetVersion'
import { useUpdateMessageSetVersion } from '../hooks/useUpdateMessageSetVersion'
import { MessageDisplayType } from '../types'
import { DynamicMessages } from './DynamicMessages'

interface IMessageSetForm {
  messageSet?: GetMessageSetV2_messageSet_MessageSetV2
  onDelete?: () => void
  onSave: (input: {
    title: string
    public: boolean
    dynamic: boolean
    rssUrl?: string
    messageIds: string[]
    isNote?: boolean
  }) => void
}

interface IMessage {
  id: string
  text: string | null
  characters: number[][] | null
  createdAt: number
  tags: string[] | null
  cardSize?: string
}

const deduplicateMessages = (messages: IMessage[]) => {
  const deduplicatedMessages: IMessage[] = []

  messages.forEach(message => {
    const foundMessage = deduplicatedMessages.find(deduplicatedMessage => {
      return deduplicatedMessage.id === message.id
    })

    if (!foundMessage) {
      deduplicatedMessages.push(message)
    }
  })

  return deduplicatedMessages
}

export const MessageSetForm = (props: IMessageSetForm) => {
  const [messageId, setMessageId] = useState<string | null>(null)
  const [createMessage] = useCreateMessage()
  const [currentVersionId, setCurrentVersionId] = useState<string | null>(props.messageSet?.currentVersionId || null)
  const [title, setTitle] = useState(props.messageSet?.title || '')
  const [isNote, setIsNote] = useState(props.messageSet?.isNote || false)
  const [isPublic, setPublic] = useState(props.messageSet?.public || false)
  const [dynamic, setDynamic] = useState(props.messageSet?.dynamic || false)
  const [rssUrl, setRssUrl] = useState(props.messageSet?.rssUrl || '')
  const [messages, setMessages] = useState<IMessage[]>(props.messageSet?.messages || [])
  const [, setPublishedMessages] = useState<IMessage[]>(props.messageSet?.messages || [])
  const [error, setError] = useState<string | null>(null)
  const history = useHistory()
  const [isAddingMessage, setIsAddingMessage] = useState(false)
  const [editingMessage, setEditingMessage] = useState<number[][] | null>(null)
  const [replacingMessageId, setReplacingMessageId] = useState<string | null>(null)
  const [insertAfterMessageId, setInsertAfterMessageId] = useState<string | null>(null)
  const { addToast } = useToasts()
  const [messagesToShow, setMessagesToShow] = useState(25)
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState<null | HTMLElement>(null)
  const [bulkTagging, setBulkTagging] = useState(false)
  const [bulkTagMessagesMutation] = useBulkTagMessages()
  const [messageSetVersion, setMessageSetVersion] = useState<string | null>(props.messageSet?.currentVersionId || null)
  const { data: messageSetVersionsData, refetch: refetchMessageSetVersions } = useListMessageSetVersions({
    input: { messageSetId: props.messageSet?.id || '', limit: 100, cursor: null }
  })
  const messageSetVersions = useMemo(() => messageSetVersionsData?.listMessageSetVersionsV2?.versions || [], [
    messageSetVersionsData
  ])
  const [createMessageSetVersion, { loading: isCreatingMessageSetVersion }] = useCreateMessageSetVersion()
  const [updateMessageSetVersion] = useUpdateMessageSetVersion()
  const [publishMessageSetVersion, { loading: isPublishingMessageSetVersion }] = usePublishMessageSetVersion()
  const { data: messageSetVersionData, loading: messageSetVersionLoading } = useGetMessageSetVersion(
    {
      //skip: !messageSetVersion,
      input: { id: messageSetVersion || '', messageSetId: props.messageSet?.id || '' }
    },
    !messageSetVersion
  )

  useEffect(() => {
    if (!messageSetVersion) {
      setMessages(props.messageSet?.messages || [])
      setPublishedMessages(props.messageSet?.messages || [])
    }

    const newMessages =
      messageSetVersionData?.getMessageSetVersionV2?.__typename === 'MessageSetVersionV2'
        ? messageSetVersionData.getMessageSetVersionV2.messages || []
        : []

    if (newMessages.length) {
      setMessages(newMessages)
    }
  }, [messageSetVersion, messageSetVersionData, props.messageSet])

  const handleSave = () => {
    setError(null)

    if (!title) {
      setError('title')
      addToast('Please enter a title', {
        appearance: 'error',
        autoDismiss: true
      })
      return
    }

    if (dynamic && !rssUrl) {
      setError('rssUrl')
      addToast('Please enter an RSS URL', {
        appearance: 'error',
        autoDismiss: true
      })
      return
    }

    props.onSave({
      title,
      public: isPublic,
      isNote,
      dynamic,
      rssUrl,
      messageIds: messages.map(message => message.id)
    })
  }

  const handleUpdateMessageSetMessages = useCallback(
    async (messages: IMessage[]) => {
      await updateMessageSetVersion({
        variables: {
          input: {
            id: messageSetVersion || '',
            messageSetId: props.messageSet?.id || '',
            messageIds: messages.map(message => message.id),
            title: messageSetVersions.find(version => version.id === messageSetVersion)?.title || ''
          }
        }
      })
    },
    [updateMessageSetVersion, messageSetVersion, props.messageSet, messageSetVersions]
  )

  const onDragEnd = useCallback(
    async (result: import('react-beautiful-dnd').DropResult) => {
      if (!result.destination) {
        return
      }

      const startIndex = result.source.index
      const endIndex = result.destination.index

      const newMessages = Array.from(messages)
      const [removed] = newMessages.splice(startIndex, 1)
      newMessages.splice(endIndex, 0, removed)
      const deduplicatedMessages = deduplicateMessages(newMessages)

      setMessages(deduplicatedMessages)
      if (messageSetVersion) {
        await handleUpdateMessageSetMessages(deduplicatedMessages)
      }
    },
    [messages, messageSetVersion, handleUpdateMessageSetMessages]
  )

  const handleCreateMessageSetVersion = async () => {
    if (!props?.messageSet?.id) {
      return
    }

    const version = await createMessageSetVersion({
      variables: {
        input: {
          title: `v${messageSetVersions.length + 1}.0`,
          messageSetId: props.messageSet.id,
          messageIds: messages.map(message => message.id)
        }
      }
    })

    if (version.data?.createMessageSetVersionV2?.__typename === 'MessageSetVersionV2') {
      setMessageSetVersion(version.data.createMessageSetVersionV2.id)
      setPublishedMessages(messages)
      addToast('Version created', {
        appearance: 'success',
        autoDismiss: true
      })
      await refetchMessageSetVersions()
    } else {
      addToast(version.data?.createMessageSetVersionV2?.error || 'There was an error creating the version', {
        appearance: 'error',
        autoDismiss: true
      })
    }
  }

  const handlePublishVersion = async () => {
    if (!props?.messageSet?.id || !messageSetVersion) {
      return
    }

    await publishMessageSetVersion({
      variables: {
        input: {
          id: messageSetVersion,
          messageSetId: props.messageSet.id
        }
      }
    })

    setCurrentVersionId(messageSetVersion)

    addToast('Version published', {
      appearance: 'success',
      autoDismiss: true
    })
    await refetchMessageSetVersions()
  }

  return (
    <>
      {messageSetVersionLoading || isPublishingMessageSetVersion || isCreatingMessageSetVersion ? <LoadMask /> : null}
      {bulkTagging ? (
        <FullModal onClose={() => setBulkTagging(false)} title='Bulk Tag'>
          <BulkTags
            onCancel={() => setBulkTagging(false)}
            onSave={async tagsUnformatted => {
              const tags = tagsUnformatted.map(tag => tag.trim().toLowerCase())
              setMessages(messages =>
                messages.map(message => ({ ...message, tags: uniq([...(message.tags || []), ...tags]) }))
              )
              await bulkTagMessagesMutation({
                variables: {
                  messageIds: messages.map(message => message.id),
                  tags
                }
              })
              setBulkTagging(false)
            }}
          />
        </FullModal>
      ) : null}
      <MessageModal
        messageId={messageId}
        onClose={() => {
          setMessageId(null)
        }}
        onUpdateTags={(messageId, tags) => {
          setMessages(messages =>
            messages.map(message => {
              if (message.id === messageId) {
                return {
                  ...message,
                  tags
                }
              }
              return message
            })
          )
          setMessageId(null)
        }}
      />
      {editingMessage ? (
        <CreateMessageModal
          boardStyle={isNote ? 'note-black' : 'black'}
          onClose={() => {
            setEditingMessage(null)
          }}
          onSaved={async message => {
            const newMessages = messages.map(m =>
              JSON.stringify(m.characters) === JSON.stringify(editingMessage) ? message : m
            )

            setMessages(deduplicateMessages(newMessages))
            setEditingMessage(null)
            if (messageSetVersion) {
              await handleUpdateMessageSetMessages(newMessages)
            }
          }}
          initialCharacters={editingMessage}
        />
      ) : null}
      {isAddingMessage || replacingMessageId || insertAfterMessageId ? (
        <CreateMessageModal
          boardStyle={isNote ? 'note-black' : 'black'}
          onClose={() => {
            setIsAddingMessage(false)
            setReplacingMessageId(null)
            setInsertAfterMessageId(null)
          }}
          onSaved={async message => {
            let newMessages: IMessage[]

            if (replacingMessageId) {
              // Replace the message
              newMessages = messages.map(m => (m.id === replacingMessageId ? message : m))
              setReplacingMessageId(null)
            } else if (insertAfterMessageId) {
              // Insert after the specified message
              const insertIndex = messages.findIndex(m => m.id === insertAfterMessageId)
              newMessages = [...messages.slice(0, insertIndex + 1), message, ...messages.slice(insertIndex + 1)]
              setInsertAfterMessageId(null)
            } else {
              // Add to the beginning
              newMessages = [message, ...messages]
            }

            setIsAddingMessage(false)
            setMessages(deduplicateMessages(newMessages))
            if (messageSetVersion) {
              await handleUpdateMessageSetMessages(newMessages)
            }
          }}
        />
      ) : null}
      <Card component={Paper}>
        <Box padding={'24px'}>
          <TextField
            label='Title'
            value={title}
            onChange={e => setTitle(e.target.value)}
            fullWidth
            variant='outlined'
            error={error === 'title'}
          />
          <Spacer size='medium' />
          <Checkbox
            checked={isPublic}
            handleCheck={() => {
              setPublic(!isPublic)
            }}
            label='Public'
          />
          <Spacer size='medium' />
          <Checkbox
            checked={dynamic}
            handleCheck={() => {
              setDynamic(!dynamic)
            }}
            label='Dynamic'
          />
          <Spacer size='medium' />
          <Checkbox
            checked={isNote}
            handleCheck={() => {
              setIsNote(!isNote)
            }}
            label='Is Note'
          />
          {dynamic ? (
            <>
              <Spacer size='medium' />
              <TextField
                error={error === 'rssUrl'}
                label='RSS URL'
                value={rssUrl}
                onChange={e => setRssUrl(e.target.value)}
                fullWidth
                variant='outlined'
              />
            </>
          ) : null}
          <Spacer size='large' />
          <FlexHorizontal>
            <Button
              onClick={() => {
                history.push('/message-sets')
              }}>
              Cancel
            </Button>
            <Box width={24} />
            <Button onClick={handleSave} variant='contained' color='primary' size='large'>
              Save
            </Button>
            <Box width={24} />
            {props.onDelete ? (
              <Button onClick={props.onDelete} variant='contained' color='secondary' size='large'>
                Delete
              </Button>
            ) : null}
          </FlexHorizontal>
        </Box>
      </Card>
      <Spacer size='extraLarge' />
      <FlexHorizontal spaceBetween={true}>
        {dynamic ? null : (
          <FlexHorizontal center>
            <Typography variant='h5'>Messages</Typography>
            <div style={{ width: 20 }} />
            <Select
              value={messageSetVersion || ''}
              onChange={e => setMessageSetVersion(e.target.value as string)}
              variant='outlined'>
              <MenuItem value=''>Published Version</MenuItem>
              {messageSetVersions.map(version => (
                <MenuItem key={version.id} value={version.id}>
                  {version.title}
                  {version.id === currentVersionId ? ' (Published)' : ''}
                </MenuItem>
              ))}
            </Select>
            {dynamic ? null : (
              <>
                <div style={{ width: 7 }} />
                <Button variant='contained' color='primary' onClick={handleCreateMessageSetVersion}>
                  Create Version
                </Button>
              </>
            )}
            {dynamic || !messageSetVersion || messageSetVersion === currentVersionId ? null : (
              <>
                <div style={{ width: 7 }} />
                <Button variant='contained' color='primary' onClick={handlePublishVersion}>
                  Publish Version
                </Button>
              </>
            )}
          </FlexHorizontal>
        )}
        {dynamic ? null : (
          <FlexHorizontal>
            <Button
              variant='outlined'
              color='default'
              endIcon={<Label />}
              onClick={() => {
                setBulkTagging(true)
              }}>
              Bulk Tag
            </Button>
            {dynamic ? null : (
              <>
                <Box width={20} />
                <CsvImportButton
                  mappings={['message']}
                  handleImport={async data => {
                    const results = await batch({
                      batchSize: 5
                    })(async (item: any) => {
                      const text = item.message.includes('[[') ? null : item.message
                      const characters = item.message.includes('[[') ? JSON.parse(item.message) : null
                      return await createMessage({
                        variables: {
                          input: {
                            text,
                            characters,
                            displayType: isNote ? MessageDisplayType.note : MessageDisplayType.flagship
                          }
                        }
                      })
                    })(data)

                    const newMessages = deduplicateMessages([
                      ...messages,
                      ...results
                        .filter(({ data }: any) => data?.createMessage.__typename === 'MessageV2')
                        .map(({ data }: any) => {
                          return data?.createMessage as IMessage
                        })
                    ])

                    setMessages(newMessages)

                    if (messageSetVersion) {
                      await handleUpdateMessageSetMessages(newMessages)
                    }
                  }}
                />
              </>
            )}
            <Box width={20} />
            <Button
              variant='outlined'
              color='default'
              endIcon={<CloudDownload />}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                setExportMenuAnchorEl(event.currentTarget)
              }}>
              Export CSV
            </Button>
            <Menu
              anchorEl={exportMenuAnchorEl}
              keepMounted
              open={Boolean(exportMenuAnchorEl)}
              onClose={() => setExportMenuAnchorEl(null)}>
              <MenuItem
                onClick={() => {
                  const csvData = messages.map(message => `"${JSON.stringify(message.characters)}"`).join('\n')
                  csvDownload(csvData)
                  setExportMenuAnchorEl(null)
                }}>
                Export as Character Codes
              </MenuItem>
              <MenuItem
                onClick={() => {
                  const csvData = csvFormat(
                    messages.map(message => ({
                      characters: JSON.stringify(message.characters),
                      text: message.text ?? characterCodesToText(message?.characters as IBoard)
                    }))
                  )
                  csvDownload(csvData)
                  setExportMenuAnchorEl(null)
                }}>
                Export as Text
              </MenuItem>
            </Menu>
            {dynamic ? null : (
              <>
                <Box width={20} />
                <Button variant='contained' onClick={() => setIsAddingMessage(true)}>
                  Add Message
                </Button>
              </>
            )}
          </FlexHorizontal>
        )}
      </FlexHorizontal>
      <Spacer size='large' />
      {dynamic ? (
        <DynamicMessages messageSetId={props?.messageSet?.id || ''} />
      ) : !messages.length ? (
        <Alert severity='info'>No messages yet. Add a message to get started.</Alert>
      ) : (
        <Card>
          <InfiniteScroll
            pageStart={0}
            loadMore={async () => {
              setMessagesToShow(messagesToShow => messagesToShow + 25)
            }}
            hasMore={messages.length > messagesToShow}
            loader={
              <Box>
                <Spacer size='large' />
                <CircularProgress />
              </Box>
            }>
            <TableContainer>
              <Table>
                <TableBody component={dynamic ? TableBody : DroppableTableBody(onDragEnd)}>
                  {messages
                    .filter((_, index) => index <= messagesToShow)
                    .map((message, index) => (
                      <TableRow component={dynamic ? TableRow : DraggableTableRow(message.id, index)} key={message.id}>
                        {dynamic ? null : (
                          <TableCell style={{ width: 40 }}>
                            <DragIndicator />
                          </TableCell>
                        )}
                        <TableCell style={{ width: 200 }}>
                          {message.text ? (
                            <>
                              <Card raised>
                                <CardContent>
                                  <Typography variant='body2'>{message.text}</Typography>
                                </CardContent>
                              </Card>
                              {dynamic ? (
                                <>
                                  <Spacer size='medium' />
                                  <FormattedDateTime value={message.createdAt} />
                                </>
                              ) : null}
                            </>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          {(message.tags || []).map((tag, index) => (
                            <Chip
                              key={`${tag}-${index}`}
                              label={tag}
                              style={{
                                margin: 3
                              }}
                            />
                          ))}
                        </TableCell>

                        {dynamic ? null : (
                          <TableCell style={{ width: 250 }}>
                            <MessageCard
                              characters={message.characters as IBoard}
                              boardStyle={message?.cardSize === '15x3' ? 'note-black' : 'black'}
                              onEditPress={() => {
                                setMessageId(message.id)
                              }}
                            />
                          </TableCell>
                        )}
                        {dynamic ? null : (
                          <TableCell style={{ width: 150 }}>
                            <FlexHorizontal center>
                              <ButtonBase
                                onClick={() => {
                                  setEditingMessage(message.characters)
                                }}
                                title='Edit this message'>
                                <Edit color='action' />
                              </ButtonBase>
                              <Box width={8} />
                              <ButtonBase
                                onClick={() => {
                                  setReplacingMessageId(message.id)
                                }}
                                title='Replace this message'>
                                <SwapHoriz color='action' />
                              </ButtonBase>
                              <Box width={8} />
                              <ButtonBase
                                onClick={() => {
                                  setInsertAfterMessageId(message.id)
                                }}
                                title='Insert message below'>
                                <AddCircle color='action' />
                              </ButtonBase>
                              <Box width={8} />
                              <ButtonBase
                                onClick={async () => {
                                  const newMessages = messages.filter(
                                    currentMessage => currentMessage.id !== message.id
                                  )
                                  setMessages(newMessages)
                                  if (messageSetVersion) {
                                    await handleUpdateMessageSetMessages(newMessages)
                                  }
                                }}
                                title='Remove this message'>
                                <RemoveCircle color='action' />
                              </ButtonBase>
                            </FlexHorizontal>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </InfiniteScroll>
        </Card>
      )}
    </>
  )
}
